import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Filter, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNotifications } from '@/contexts/NotificationContext';
import { useNotificationsList } from '@/hooks/useNotificationsList';
import { NotificationItem } from '@/components/notifications/NotificationItem';
import { NotificationPermissionBanner } from '@/components/notifications/NotificationPermissionBanner';
import { Notification } from '@/contexts/NotificationContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { linkVanVerzoek } from '@/features/projectpagina/data/feedback';

type FilterType = 'all' | 'unread' | 'lead' | 'todo' | 'ticket' | 'project_note';

export default function CRMNotifications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const { isSubscribed, isSupported } = usePushNotifications();
  const [filter, setFilter] = useState<FilterType>('all');
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Determine query options based on filter
  const queryOptions = filter === 'all' 
    ? {} 
    : filter === 'unread'
    ? { isRead: false }
    : { type: filter };

  const { notifications, loading } = useNotificationsList(queryOptions);

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navigate based on type
    switch (notification.type) {
      case 'lead':
        navigate('/crm/leads');
        break;
      case 'todo':
        navigate('/crm/todos');
        break;
      case 'ticket':
        navigate('/crm/tickets');
        break;
      case 'project':
        navigate('/crm/projects');
        break;
      case 'project_note':
        navigate('/crm/projects');
        break;
      case 'feedback': {
        // Naar de Versie in de documentweergave op de projectpagina (ADR 0021).
        const link = notification.reference_id
          ? await linkVanVerzoek(supabase as never, notification.reference_id).catch(() => null)
          : null;
        navigate(link ?? '/crm/projects');
        break;
      }
      case 'ems':
        // Datakwaliteit van de Energiemonitor (migratie 101): reference_id is de locatie.
        navigate(notification.reference_id ? `/crm/ems/${notification.reference_id}` : '/crm/sites');
        break;
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    toast.success('Alle notificaties gemarkeerd als gelezen');
  };

  const handleDelete = async (notificationId: string) => {
    await deleteNotification(notificationId);
    toast.success('Notificatie verwijderd');
  };

  const handleTestPushNotification = async () => {
    if (!user) return;
    
    setIsSendingTest(true);
    try {
      const { error } = await supabase.functions.invoke('push-notifications', {
        body: {
          userId: user.id,
          title: '🔔 Test Push Notificatie',
          body: 'Dit is een test notificatie. Als je dit ziet, werken push notificaties!',
          url: '/crm/notifications',
          data: { test: true },
        },
      });

      if (error) {
        throw error;
      }

      toast.success('Test notificatie verzonden! Check je apparaat.');
    } catch (error) {
      console.error('Test notification error:', error);
      toast.error('Kon test notificatie niet verzenden');
    } finally {
      setIsSendingTest(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      {/* Push Notification Permission Banner */}
      {isSupported && <NotificationPermissionBanner />}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Notificaties</h1>
            {unreadCount > 0 && (
              <p className="text-sm text-muted-foreground">
                {unreadCount} ongelezen {unreadCount === 1 ? 'notificatie' : 'notificaties'}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isSubscribed && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleTestPushNotification}
              disabled={isSendingTest}
              className="gap-2"
            >
              <Send className="h-4 w-4" />
              {isSendingTest ? 'Verzenden...' : 'Test Push'}
            </Button>
          )}
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="gap-2"
            >
              <Check className="h-4 w-4" />
              Alles markeren
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterType)} className="mb-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="all">Alle</TabsTrigger>
          <TabsTrigger value="unread">Ongelezen</TabsTrigger>
          <TabsTrigger value="lead">Leads</TabsTrigger>
          <TabsTrigger value="todo">To-do's</TabsTrigger>
          <TabsTrigger value="ticket">Tickets</TabsTrigger>
          <TabsTrigger value="project_note">Projecten</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Notifications list */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">
            Laden...
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-12 text-center">
            <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">Geen notificaties</h3>
            <p className="text-sm text-muted-foreground">
              {filter === 'unread'
                ? 'Je hebt alle notificaties gelezen'
                : filter === 'all'
                ? 'Je hebt nog geen notificaties ontvangen'
                : `Je hebt geen ${filter} notificaties`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={handleNotificationClick}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
